import React, { useCallback, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import {
  loadEmployeesAcrossBranches,
  filterJoinersInRange,
  filterLeaversInRange,
  filterAnniversaryByDojMonth,
} from "../../helpers/payrollReportData";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || "").trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function PayrollTenureMobilityReport() {
  const today = new Date();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rangeStart, setRangeStart] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`
  );
  const [rangeEnd, setRangeEnd] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  );
  const [anniMonth, setAnniMonth] = useState(String(today.getMonth()));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadEmployeesAcrossBranches(getStoredUser());
      setAll(data);
      toast.success(`Loaded ${data.length} employee(s)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const rs = parseYmd(rangeStart);
  const re = parseYmd(rangeEnd);

  const joiners = useMemo(() => {
    if (!all.length || !rs || !re) return [];
    return filterJoinersInRange(all, rs, re);
  }, [all, rs, re]);

  const leavers = useMemo(() => {
    if (!all.length || !rs || !re) return [];
    return filterLeaversInRange(all, rs, re);
  }, [all, rs, re]);

  const anniversary = useMemo(() => {
    if (!all.length) return [];
    return filterAnniversaryByDojMonth(all, parseInt(anniMonth, 10));
  }, [all, anniMonth]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
            <h1 className="text-2xl font-bold text-slate-900">Joiners, leavers & DOJ anniversary</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Joiners use <strong>DOJ</strong> fields on employee docs. Leavers need status <strong>Left</strong> and a
              relieving/leaving date in range.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <label className="text-xs font-semibold text-slate-700">
            Range start
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5"
            />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Range end
            <input
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5"
            />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Anniversary (DOJ month)
            <select
              value={anniMonth}
              onChange={(e) => setAnniMonth(e.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5"
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={String(i)}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <section>
            <h2 className="text-sm font-bold text-slate-900">New joiners in range ({joiners.length})</h2>
            <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white text-xs">
              <table className="min-w-full">
                <thead className="bg-slate-100 text-[10px] font-bold uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Branch</th>
                    <th className="px-2 py-1.5 text-left">Name</th>
                    <th className="px-2 py-1.5 text-left">DOJ</th>
                  </tr>
                </thead>
                <tbody>
                  {joiners.map((r, i) => (
                    <tr key={`j-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.branchLabel}</td>
                      <td className="px-2 py-1">{r.name}</td>
                      <td className="px-2 py-1 font-mono">{r.dojYmd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h2 className="text-sm font-bold text-slate-900">Leavers in range ({leavers.length})</h2>
            <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white text-xs">
              <table className="min-w-full">
                <thead className="bg-slate-100 text-[10px] font-bold uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Branch</th>
                    <th className="px-2 py-1.5 text-left">Name</th>
                    <th className="px-2 py-1.5 text-left">Leaving</th>
                  </tr>
                </thead>
                <tbody>
                  {leavers.map((r, i) => (
                    <tr key={`l-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.branchLabel}</td>
                      <td className="px-2 py-1">{r.name}</td>
                      <td className="px-2 py-1 font-mono">{r.leavingYmd || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h2 className="text-sm font-bold text-slate-900">DOJ in {MONTHS[parseInt(anniMonth, 10)]} ({anniversary.length})</h2>
            <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white text-xs">
              <table className="min-w-full">
                <thead className="bg-slate-100 text-[10px] font-bold uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Branch</th>
                    <th className="px-2 py-1.5 text-left">Name</th>
                    <th className="px-2 py-1.5 text-left">DOJ</th>
                  </tr>
                </thead>
                <tbody>
                  {anniversary.map((r, i) => (
                    <tr key={`a-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.branchLabel}</td>
                      <td className="px-2 py-1">{r.name}</td>
                      <td className="px-2 py-1 font-mono">{r.dojYmd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
